import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Container,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

interface User {
  _id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string;
  email?: string;
}

interface SearchResponse {
  success: boolean;
  data: User[];
  message?: string;
}

const UserSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce search API calls
  useEffect(() => {
    console.log('Search term changed:', searchTerm);
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 3) {
        console.log('Triggering search for:', searchTerm);
        performSearch(searchTerm);
      } else {
        console.log('Search term too short, clearing results');
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const performSearch = async (query: string) => {
    console.log('Performing search for:', query);
    setLoading(true);
    try {
      const url = `/profile/search?q=${encodeURIComponent(query)}&page=1&limit=5`;
      console.log('API URL:', url);
      
      const response = await request<any>(url, {
        method: 'GET',
        authorization: true,
      });

      console.log('API Response:', response);

      if (response.status === true) {
        console.log('Search results:', response.data);
        setSearchResults(response.data);
        setShowDropdown(true);
      } else {
        console.log('No results or failed response:', response);
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('User search error:', error);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user: User) => {
    setShowDropdown(false);
    navigate(`/profile/${user._id}`);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    console.log('Input changed:', value, 'Length:', value.length);
    setSearchTerm(value);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      <TopNavBar title="Search Users" canGoBack onBackClick={handleBackClick} />
      
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Box sx={{ position: 'relative' }} ref={searchRef}>
          <TextField
            fullWidth
            placeholder="Search users by name or username..."
            value={searchTerm}
            onChange={handleInputChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '25px',
                backgroundColor: '#f5f5f5',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SearchIcon color="action" />
                  )}
                </InputAdornment>
              ),
            }}
          />

          {/* Dropdown Results */}
          {(() => {
            console.log('Dropdown render check:', { showDropdown, resultsLength: searchResults.length, searchResults });
            return null;
          })()}
          
          {/* Debug: Always show dropdown if there are results */}
          {(searchResults.length > 0 || showDropdown) && (
            <Paper
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                maxHeight: '400px',
                overflow: 'auto',
                zIndex: 1000,
                boxShadow: 3,
                borderRadius: 2,
                border: '1px solid #ccc', // Add border for debugging
              }}
            >
              <List>
                {searchResults.map((user:any) => (
                  <ListItem
                    key={user._id}
                    onClick={() => handleUserClick(user)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={user.profilePhoto && user.profilePhoto?.url}
                        sx={{ bgcolor: 'primary.main' }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
                      secondary={user.username !== `${user.firstName || ''} ${user.lastName || ''}`.trim() ? user.username : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* No Results */}
          {showDropdown && searchTerm.length >= 3 && searchResults.length === 0 && !loading && (
            <Paper
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                mt: 1,
                zIndex: 1000,
                boxShadow: 3,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No users found for "{searchTerm}"
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Instructions */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Type at least 3 characters to search for users
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UserSearch;
